// src/context/AuthContext.tsx

import React, { createContext, useContext, useState } from "react";
import type { User, UserRole } from "@/types";

// ── Mock users for each role (Section 8.2) ───────────────────
const MOCK_USERS: Record<UserRole, User> = {
  mrn_admin: {
    id:             "u-001",
    name:           "Alex Rivera",
    email:          "alex.rivera@mrn.com",
    role:           "mrn_admin",
    orgName:        "MRN",
    orgId:          "org-mrn",
    avatarInitials: "AR",
  },
  retailer_admin: {
    id:             "u-002",
    name:           "Jordan Kim",
    email:          "jordan.kim@midwesthardware.com",
    role:           "retailer_admin",
    orgName:        "Midwest Hardware",
    orgId:          "org-mhw",
    avatarInitials: "JK",
  },
  retailer_analyst: {
    id:             "u-003",
    name:           "Sam Patel",
    email:          "sam.patel@midwesthardware.com",
    role:           "retailer_analyst",
    orgName:        "Midwest Hardware",
    orgId:          "org-mhw",
    avatarInitials: "SP",
  },
  brand_manager: {
    id:             "u-004",
    name:           "Casey Morgan",
    email:          "casey.morgan@dewalt.com",
    role:           "brand_manager",
    orgName:        "DeWalt Tools",
    orgId:          "org-dewalt",
    avatarInitials: "CM",
  },
  brand_analyst: {
    id:             "u-005",
    name:           "Taylor Brooks",
    email:          "taylor.brooks@dewalt.com",
    role:           "brand_analyst",
    orgName:        "DeWalt Tools",
    orgId:          "org-dewalt",
    avatarInitials: "TB",
  },
  agency_user: {
    id:             "u-006",
    name:           "Morgan Chen",
    email:          "morgan.chen@omnicommedia.com",
    role:           "agency_user",
    orgName:        "Omnicom Media Group",
    orgId:          "org-omnicom",
    avatarInitials: "MC",
  },
};

// ── Context types ────────────────────────────────────────────
interface AuthContextValue {
  user:     User | null;
  isAuthed: boolean;
  login:    (role: UserRole) => void;
  logout:   () => void;
  can:      (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(MOCK_USERS.mrn_admin);

  const login  = (role: UserRole) => setUser(MOCK_USERS[role]);
  const logout = () => setUser(null);
  const can    = (roles: UserRole[]) => !!user && roles.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, isAuthed: !!user, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}